import React, { useEffect, useState } from 'react';
import { CheckCircle, AlertCircle, Loader2, Mail, ArrowLeft } from 'lucide-react';
import { AppLogo } from '../Layout/AppLogo';
import { supabase, clearAllAuthData } from '../../lib/supabase';

interface EmailConfirmationProps {
  onBackToLogin: () => void;
}

export const EmailConfirmation: React.FC<EmailConfirmationProps> = ({ onBackToLogin }) => {
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const handleEmailConfirmation = async () => {
      try {
        console.log('🔵 Auth: Starting email confirmation process');
        console.log('🔵 Auth: Current URL:', window.location.href);
        
        const { data, error } = await supabase.auth.getSessionFromUrl();


        if (error) {
          console.error('🔴 Auth: Error from getSessionFromUrl:', error);
          // Clear any stale auth tokens if we get a refresh token error
          if (error.message?.includes('refresh_token') || 
              error.message?.includes('Refresh Token') ||
              error.code === 'refresh_token_not_found') {
            console.log('🔄 Clearing stale auth tokens due to refresh token error');
            await clearAllAuthData();
          }
          setStatus('error');
          setMessage(error.message || 'Account confirmation failed.');
          return;
        }

        if (data.session) {
          console.log('✅ Auth: Session confirmed successfully');
          setStatus('success');
          setMessage('Your account has been confirmed');
          
          // Auto redirect to login after 3 seconds
          setTimeout(() => {
            onBackToLogin();
          }, 3000);
        } else {
          console.warn('⚠️ Auth: No session in response');
          setStatus('error');
          setMessage('No session found in confirmation link.');
        }
      } catch (error) {
        console.error('🔴 Auth: Unexpected error:', error);
        setStatus('error');
        setMessage('An unexpected error occurred during confirmation.');
      }
    };

    handleEmailConfirmation();
  }, [onBackToLogin]);

  const getStatusIcon = () => {
    switch (status) {
      case 'loading':
        return <Loader2 className="h-16 w-16 text-modular-accent-blue animate-spin" />;
      case 'success':
        return <CheckCircle className="h-16 w-16 text-emerald-500" />;
      case 'error':
      default:
        return <AlertCircle className="h-16 w-16 text-red-500" />;
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'success':
        return 'text-emerald-600 dark:text-emerald-400';
      case 'error':
        return 'text-red-600 dark:text-red-400';
      case 'loading':
      default:
        return 'text-modular-accent-blue dark:text-modular-accent-blue';
    }
  };

  const getStatusTitle = () => {
    switch (status) {
      case 'loading':
        return 'Confirming Your Account…';
      case 'success':
        return 'Your account has been confirmed';
      case 'error':
      default:
        return 'Confirmation Failed';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-modular-shadow-quartz via-modular-graphite-smoke to-modular-frosted-indigo 
      dark:bg-dark-gradient flex items-center justify-center p-4 transition-all duration-500">
      
      <div className="w-full max-w-md">
        <div className="bg-white/95 dark:bg-dark-surface/95 backdrop-blur-xl rounded-2xl 
          border border-modular-stone-alloy/30 dark:border-dark-border/50 p-8 shadow-lg text-center">
          
          {/* Logo */}
          <div className="flex items-center justify-center mb-6">
            <AppLogo size="lg" animated={true} />
            <div className="ml-4">
              <h1 className="text-3xl font-bold text-gray-900 dark:text-dark-text">
                PromptOS
              </h1>
              <p className="text-lg text-modular-accent-blue dark:text-modular-accent-blue font-medium">
                AI Prompt Operating System
              </p>
            </div>
          </div>

          {/* Status Icon */}
          <div className="flex justify-center mb-6">
            {getStatusIcon()}
          </div>

          {/* Status Message */}
          <div className="mb-8">
            <h2 className={`text-xl font-semibold mb-3 ${getStatusColor()}`}>
              {getStatusTitle()}
            </h2>
            <p className="text-gray-600 dark:text-dark-text-secondary leading-relaxed">
              {message}
            </p>
          </div>

          {/* Action Buttons */}
          <div className="space-y-4">
            {status === 'success' && (
              <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 
                dark:border-emerald-800 rounded-lg p-4 mb-4">
                <div className="flex items-center text-emerald-700 dark:text-emerald-300">
                  <Mail className="h-5 w-5 mr-2" />
                  <span className="text-sm font-medium">
                    Redirecting to login in 3 seconds...
                  </span>
                </div>
              </div>
            )}

            <button
              onClick={onBackToLogin}
              className="w-full bg-modular-primary-gradient text-white font-semibold 
                py-3 px-6 rounded-lg focus:outline-none focus:ring-2 focus:ring-modular-accent-blue 
                focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-dark-surface 
                transition-all duration-300 shadow-modular-card hover:shadow-modular-glow 
                transform hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              {status === 'success' ? 'Go to Login' : 'Back to Login'}
            </button>
          </div>

          {/* Help Text */}
          <div className="mt-6 text-center">
            <div className="text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-dark-card 
              rounded-lg p-3 border border-gray-200 dark:border-dark-border">
              <p className="mb-1 font-medium">Need help with PromptOS?</p>
              <p>If you continue to have issues, please check your email for the most recent confirmation link.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};